'use client';
import Link from 'next/link';

import { useState, type FormEvent } from 'react';

import { trackHomepageEvent } from '@/components/public/homepage-analytics';

type HomepageNewsletterFormProps = {
  sourceSlug: string;
  submitLabel: string;
};

export function HomepageNewsletterForm({
  sourceSlug,
  submitLabel,
}: HomepageNewsletterFormProps) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [message, setMessage] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const cleanEmail = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      setStatus('error');
      setMessage('Please enter a valid email address.');
      void trackHomepageEvent('home_newsletter_submit', { status: 'invalid', source_slug: sourceSlug });
      return;
    }

    try {
      setStatus('saving');
      setMessage(null);
      const response = await fetch('/api/newsletter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: cleanEmail,
          consent: true,
          sourceSlug,
          sourceType: 'homepage',
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'Unable to subscribe right now.');
      setStatus('saved');
      setMessage(payload.message || 'Request recorded. Email confirmation is required.');
      setEmail('');
      void trackHomepageEvent('home_newsletter_submit', { status: 'saved', source_slug: sourceSlug });
    } catch (error) {
      setStatus('error');
      setMessage(error instanceof Error ? error.message : 'Unable to subscribe right now.');
      void trackHomepageEvent('home_newsletter_submit', { status: 'error', source_slug: sourceSlug });
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col justify-center gap-3 p-8 lg:p-10">
      <label className="sr-only" htmlFor="homepage-email">Email address</label>
      {/* suppressHydrationWarning: password-manager / autofill extensions inject a background-image
          icon on the email input and an overlay button sibling after SSR but before hydration, which
          would otherwise trip a hydration mismatch. This tolerates that out-of-React DOM mutation. */}
      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto]" suppressHydrationWarning>
        <input
          id="homepage-email"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="Your email address"
          autoComplete="email"
          required
          suppressHydrationWarning
          className="h-12 rounded-[.45rem] border border-[#ead8c8] bg-white px-5 text-[14px] text-[#2f2d2a] outline-none transition placeholder:text-[#9d8c80] focus:border-[#5f8769]"
        />
        <button
          type="submit"
          disabled={status === 'saving'}
          className="h-12 rounded-[.45rem] bg-[#5f8769] px-7 text-[13px] font-semibold text-white transition hover:bg-[#547a5f] disabled:cursor-not-allowed disabled:opacity-65"
        >
          {status === 'saving' ? 'Saving...' : submitLabel}
        </button>
      </div>
      <p className="text-[12px] text-[#6d6258]">No spam. <Link href="/newsletter" className="underline">Manage newsletter preferences</Link>.</p>
      {message ? (
        <p className={`rounded-[.45rem] px-4 py-3 text-[13px] leading-5 ${status === 'error' ? 'border border-red-200 bg-red-50 text-red-700' : 'border border-green-200 bg-green-50 text-green-700'}`}>
          {message}
        </p>
      ) : null}
    </form>
  );
}
